import { PageHeader } from "@/components/ui/design-system";
import { HistoricalWorkerCreate } from "@/components/radnici/HistoricalWorkerCreate";
import "@/components/dokumenti/InboxDense.css";
import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { CompanyUploadedReview } from "@/components/dokumenti/CompanyUploadedReview";
import { deleteCompanyInboxItem, getCompanyInboxSites, obligationInboxTarget, uploadCompanyInboxFiles } from "@/lib/companyInbox";
import type { ObvezaTvrtke } from "@/lib/companyObligations";
import { InboxFileQueue, type QueuedInboxFile } from "@/components/dokumenti/InboxFileQueue";
import { InboxUploadPreview, type LocalInboxPreview } from "@/components/dokumenti/InboxUploadPreview";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { COMPANY_CATEGORIES, deleteInboxItem, getInboxSites, groupInboxFiles, uploadInboxFiles, validateInboxFile, type InboxOwner, type InboxSite, type InboxUploadSource, type InboxUploadResult } from "@/lib/documentInbox";
import { dohvatiRadnike, workerChoiceLabel, type Radnik } from "@/lib/radnici";

interface UploadOutcome {
  id: string;
  name: string;
  status: "success" | "duplicate" | "error";
  message: string;
  /** Present only for an accepted entry, which is the only kind that can be withdrawn. */
  inboxId?: string;
  company?: boolean;
  undone?: boolean;
  preview?: LocalInboxPreview;
  previewError?: string;
}
/**
 * What the row says once it has been withdrawn.
 *
 * The same sheet scanned twice leaves a duplicate marker behind, and that
 * marker goes with the original. Saying so keeps the count on the screen
 * honest: the operator would otherwise see one row undone and the tray two
 * entries lighter.
 */
function porukaPonistenja(duplikati: number): string {
  if (duplikati < 1) return "Poništeno.";
  return `Poništeno (+${duplikati} ${duplikati === 1 ? "duplikat" : "duplikata"}).`;
}

const SELECT_CLASS = "h-11 w-full rounded-md border border-border bg-background px-3";

/** Read an upload result strictly before retaining a preview or offering undo. */
function procitajRezultat(
  id: string,
  name: string,
  result: InboxUploadResult | undefined,
): UploadOutcome {
  if (!result || result.error) {
    return { id, name, status: "error", message: result?.error?.message ?? "Poslužitelj nije vratio rezultat. Pokušajte ponovno." };
  }
  if ((result.status !== "novo" && result.status !== "duplikat") || typeof result.inbox_id !== "string" || !result.inbox_id.trim()) {
    return { id, name, status: "error", message: "Poslužitelj nije potvrdio zaprimanje datoteke. Pokušajte ponovno." };
  }
  return {
    id,
    name,
    status: result.status === "duplikat" ? "duplicate" : "success",
    message: result.status === "duplikat" ? "Duplikat — dokument već postoji." : "Zaprimljeno u ladicu.",
    // Only an accepted entry offers "Poništi": a duplicate owns no document of
    // its own, and a failure left nothing behind to withdraw. What the upload
    // answered is all this row ever says - the classifier moves the entry to
    // `predlozeno` seconds later and nothing here re-reads it, so the button
    // stays where it is and the server decides whether the entry may still go.
    inboxId: result.status === "novo" ? result.inbox_id : undefined,
  };
}

export function DocumentInboxUploadPage() {
  const { can } = useAuth();
  const [uploadedIds, setUploadedIds] = useState<string[]>([]);
  if (!can("dokument", "inbox_upload") && !can("company_document", "upload")) return <Navigate to="/" replace />;
  if (uploadedIds.length) return <CompanyUploadedReview ids={uploadedIds} onFinished={() => setUploadedIds([])} />;
  return <>{can("dokument", "inbox_upload") && <Link className="mb-3 inline-flex min-h-11 items-center self-start rounded-md border border-border px-3" to="/ladica/uvoz-paketa">Uvoz paketa agencije</Link>}<InboxUploadForm onCompanyUploaded={can("company_document", "read") && can("company_document", "confirm") ? setUploadedIds : undefined} /></>;
}

export function InboxUploadForm({ obligation, onCompanyUploaded, compact = false }: { obligation?: ObvezaTvrtke; onCompanyUploaded?: (ids: string[]) => void; compact?: boolean }) {
  const { can } = useAuth();
  const canWorker = can("dokument", "inbox_upload") && !obligation;
  const canCompany = can("company_document", "upload");
  const [ownerKind, setOwnerKind] = useState<InboxOwner["owner_kind"]>(obligation?.gradiliste_id ? "gradiliste" : canWorker ? "radnik" : "firma");
  const [query, setQuery] = useState("");
  const [workers, setWorkers] = useState<Radnik[]>([]);
  const [worker, setWorker] = useState<Radnik | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [sites, setSites] = useState<InboxSite[] | null>(obligation?.gradiliste_id ? [{ id: obligation.gradiliste_id, naziv: obligation.holder_name }] : null);
  const [siteId, setSiteId] = useState(obligation?.gradiliste_id ?? "");
  const [siteError, setSiteError] = useState<string | null>(null);
  const [category, setCategory] = useState("");
  const [otherCategory, setOtherCategory] = useState("");
  const [items, setItems] = useState<QueuedInboxFile[]>([]);
  const [outcomes, setOutcomes] = useState<UploadOutcome[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewUrls = useRef(new Map<string, string>());
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    const urls = previewUrls.current;
    return () => {
      mounted.current = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  const [undoing, setUndoing] = useState<Set<string>>(new Set());
  const undoingRef = useRef(new Set<string>());

  // Keep files only after a confirmed upload result. URL creation stays outside
  // React updaters (which StrictMode can replay), and late responses cannot leak.
  function appendOutcomes(results: UploadOutcome[], files: Map<string, File>) {
    if (!mounted.current) return;
    const retained = results.map((result): UploadOutcome => {
      const file = files.get(result.id);
      if (result.status === "error" || !file) return result;
      try {
        const mimeType = file.type;
        const url = URL.createObjectURL(file);
        const previous = previewUrls.current.get(result.id);
        if (previous) URL.revokeObjectURL(previous);
        previewUrls.current.set(result.id, url);
        return { ...result, company: ownerKind !== "radnik", preview: { url, mimeType } };
      } catch {
        return { ...result, company: ownerKind !== "radnik", previewError: "Lokalni pregled nije dostupan. Dokument je poslan." };
      }
    });
    setOutcomes((current) => [...current, ...retained]);
    const last = retained.filter((result) => result.preview).at(-1);
    if (last) setPreviewId(last.id);
  }

  useEffect(() => {
    if (ownerKind !== "radnik" || worker || !query.trim()) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setSearching(true);
      dohvatiRadnike({ q: query.trim(), limit: 20, status: "svi" }, controller.signal)
        .then((page) => { if (!controller.signal.aborted) setWorkers(page.items.filter((item) => item.id)); })
        .catch((error: unknown) => { if (!controller.signal.aborted) setSearchError(errorMessage(error, "Pretraga djelatnika nije uspjela.")); })
        .finally(() => { if (!controller.signal.aborted) setSearching(false); });
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [query, ownerKind, worker]);

  useEffect(() => {
    if (ownerKind !== "gradiliste" || obligation?.gradiliste_id) return;
    const controller = new AbortController();
    (canCompany ? getCompanyInboxSites(controller.signal) : getInboxSites(controller.signal))
      .then((value) => { if (!controller.signal.aborted) { setSites(value); setSiteError(null); } })
      .catch((error: unknown) => { if (!controller.signal.aborted) setSiteError(errorMessage(error, "Gradilišta se ne mogu učitati.")); });
    return () => controller.abort();
  }, [ownerKind, canCompany, obligation?.gradiliste_id]);

  const owner: InboxOwner | null = ownerKind === "radnik"
    ? worker?.id ? { owner_kind: "radnik", radnik_id: worker.id } : null
    : ownerKind === "gradiliste"
      ? siteId ? { owner_kind: "gradiliste", gradiliste_id: siteId } : null
      : category && (category !== "ostalo" || otherCategory.trim())
        ? { owner_kind: "firma", firma_kategorija: category === "ostalo" ? `ostalo: ${otherCategory.trim()}` : category }
        : null;
  const upload = (files: File[], selectedOwner: InboxOwner, source: InboxUploadSource, batch: string) => selectedOwner.owner_kind === "radnik"
    ? uploadInboxFiles(files, selectedOwner, source, batch)
    : obligation ? uploadCompanyInboxFiles(files, selectedOwner, source, batch, obligationInboxTarget(obligation)) : uploadCompanyInboxFiles(files, selectedOwner, source, batch);
  const sendable = items.filter((item) => !validateInboxFile(item.file));
  function addFiles(files: File[], source: InboxUploadSource) {
    if (sending.current) return;
    setItems((current) => [...current, ...files.map((file): QueuedInboxFile => ({
      id: crypto.randomUUID(), file, source, status: "waiting", error: validateInboxFile(file),
    }))]);
  }

  async function send() {
    if (sending.current || !owner || !sendable.length) return;
    sending.current = true;
    setBusy(true);
    const batchId = crypto.randomUUID();
    const acceptedIds: string[] = [];
    const sendingIds = new Set(sendable.map((item) => item.id));
    setOutcomes((current) => current.filter((item) => !sendingIds.has(item.id)));
    setItems((current) => current.map((item) => sendingIds.has(item.id) ? { ...item, status: "waiting", error: null } : item));
    try {
      for (const group of groupInboxFiles(sendable)) {
        const ids = new Set(group.map((item) => item.id));
        setItems((current) => current.map((item) => ids.has(item.id) ? { ...item, status: "sending" } : item));
        let resolved: UploadOutcome[];
        try {
          const response = await upload(group.map((item) => item.file), owner, group[0]!.source, batchId);
          resolved = group.map((item, index) =>
            procitajRezultat(item.id, item.file.name, response.results.find((entry) => entry.index === index)),
          );
        } catch (error: unknown) {
          const message = errorMessage(error, "Slanje nije uspjelo. Pokušajte ponovno.");
          resolved = group.map((item) => ({ id: item.id, name: item.file.name, status: "error", message }));
        }
        acceptedIds.push(...resolved.flatMap((result) => result.inboxId ? [result.inboxId] : []));
        const byId = new Map(resolved.map((result) => [result.id, result]));
        appendOutcomes(resolved, new Map(group.map((item) => [item.id, item.file])));
        setItems((current) => current.flatMap((item) => {
          const result = byId.get(item.id);
          if (!result) return [item];
          return result.status === "error" ? [{ ...item, status: "error" as const, error: result.message }] : [];
        }));
      }
    } finally {
      sending.current = false;
      setBusy(false);
    }
    if (owner.owner_kind !== "radnik" && acceptedIds.length) onCompanyUploaded?.(acceptedIds);
  }

  /** Withdraw a newly accepted entry while keeping its result visible. */
  const [undoTarget, setUndoTarget] = useState<UploadOutcome | null>(null);
  const canDelete = can("dokument", "inbox_delete");
  async function undo(outcome: UploadOutcome, reason: string) {
    if (!canDelete || !outcome.inboxId || undoingRef.current.has(outcome.id)) return;
    undoingRef.current.add(outcome.id);
    setUndoing((current) => new Set(current).add(outcome.id));
    try {
      const duplikati = await (outcome.company ? deleteCompanyInboxItem(outcome.inboxId, reason) : deleteInboxItem(outcome.inboxId, reason));
      setUndoTarget(null);
      setPreviewId((current) => current === outcome.id ? null : current);
      setOutcomes((current) => current.map((item) =>
        item.id === outcome.id
          ? { ...item, undone: true, inboxId: undefined, message: porukaPonistenja(duplikati) }
          : item,
      ));
    } catch (error: unknown) {
      setOutcomes((current) => current.map((item) =>
        item.id === outcome.id
          ? { ...item, message: errorMessage(error, "Poništavanje nije uspjelo. Pokušajte ponovno.") }
          : item,
      ));
    } finally {
      undoingRef.current.delete(outcome.id);
      setUndoing((current) => {
        const next = new Set(current);
        next.delete(outcome.id);
        return next;
      });
    }
  }

  function nextOwner() {
    if (sending.current || items.length) return;
    previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrls.current.clear();
    setPreviewId(null);
    setOwnerKind(canWorker ? "radnik" : "firma"); setQuery(""); setWorker(null); setWorkers([]);
    setCategory(""); setOtherCategory(""); setSiteId(""); setOutcomes([]);
    setSearchError(null); setSearching(false);
  }

  const activePreview = outcomes.find((outcome) => outcome.id === previewId);
  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 lg:min-h-0 lg:max-w-none lg:flex-1">
      {!compact && <PageHeader title="Unos dokumenata" meta="Odaberite kome dokumenti pripadaju i pošaljite ih u ulaznu ladicu."/>}
      <div className={`inbox-dense grid min-w-0 gap-6 lg:min-h-0 lg:flex-1 ${compact ? "min-h-0 flex-1 overflow-y-auto" : "lg:grid-cols-[minmax(0,42fr)_minmax(0,58fr)]"} lg:gap-4`}>
      <div role="region" aria-label="Radni dio unosa" className="flex min-w-0 flex-col gap-6 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:pr-2 [&>*]:shrink-0">
      <section className="avm-surface rounded-[var(--radius-card)] border border-border bg-card p-4 md:p-6">
        <fieldset disabled={busy} className="flex min-w-0 flex-col gap-4">
          <legend className="mb-4 text-[var(--text-card)] font-semibold">1. Nositelj dokumenata</legend>
          {!obligation && <div className="flex flex-wrap gap-2" role="group" aria-label="Vrsta nositelja">
            {([ ["radnik", "Djelatnik"], ["firma", "Firma"], ["gradiliste", "Gradilište"] ] as const).filter(([kind]) => kind === "radnik" ? canWorker : canCompany).map(([kind, label]) => (
              <Button key={kind} type="button" variant={ownerKind === kind ? "default" : "outline"} aria-pressed={ownerKind === kind} onClick={() => setOwnerKind(kind)}>{label}</Button>
            ))}
          </div>}
          {obligation && <p><strong>{obligation.name_hr}</strong> · {obligation.holder_name}</p>}
          {ownerKind === "radnik" ? <div className="flex flex-col gap-2">
            <Label htmlFor="inbox-worker">Pronađite djelatnika po imenu ili OIB-u</Label>
            <Input id="inbox-worker" value={query} autoComplete="off" onChange={(event) => {
              setQuery(event.target.value); setWorker(null); setWorkers([]); setSearchError(null); setSearching(false);
            }} />
            {worker ? <p>Odabran: <strong>{workerChoiceLabel(worker)}</strong> · OIB: {worker.oib ?? "nije dostupan"}</p> : <>
              {searching ? <p role="status">Pretraživanje…</p> : null}
              {searchError ? <Alert variant="error">{searchError}</Alert> : null}
              <ul className="ui-choice-list">{workers.map((item) => <li key={item.id}>
                <Button type="button" variant="outline" className="h-auto min-h-11 w-full justify-start whitespace-normal text-left" onClick={() => { setWorker(item); setWorkers([]); setSearching(false); }}>
                  {workerChoiceLabel(item)} · OIB: {item.oib ?? "nije dostupan"}
                </Button>
              </li>)}</ul>
              <p className="text-sm text-muted-foreground">Odaberite djelatnika iz rezultata pretrage.</p>
            </>}
            <HistoricalWorkerCreate disabled={busy} onCreated={(created) => { setWorker(created); setWorkers([]); setQuery(""); setSearching(false); setSearchError(null); }} />
          </div> : ownerKind === "firma" ? <div className="flex flex-col gap-2">
            <Label htmlFor="inbox-category">Kategorija dokumenta firme</Label>
            <select aria-required="true" data-required-empty={!category} id="inbox-category" className={SELECT_CLASS} value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="">Odaberite kategoriju</option>
              {Object.entries(COMPANY_CATEGORIES).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
            {category === "ostalo" ? <><Label htmlFor="inbox-other">Opišite kategoriju</Label><Input aria-required="true" data-required-empty={!otherCategory.trim()} id="inbox-other" maxLength={240} value={otherCategory} onChange={(event) => setOtherCategory(event.target.value)} /></> : null}
          </div> : <div className="flex flex-col gap-2">
            <Label htmlFor="inbox-site">Odaberite gradilište</Label>
            <select aria-required="true" data-required-empty={!siteId} id="inbox-site" disabled={Boolean(obligation)} className={SELECT_CLASS} value={siteId} onChange={(event) => setSiteId(event.target.value)}>
              <option value="">{sites === null ? "Učitavanje gradilišta…" : "Odaberite gradilište"}</option>
              {sites?.map((site) => <option key={site.id} value={site.id}>{site.naziv}</option>)}
            </select>
            {siteError ? <Alert variant="error">{siteError}</Alert> : null}
          </div>}
        </fieldset>
      </section>
      <div className="avm-surface rounded-[var(--radius-card)] border border-border bg-card p-4 md:p-6">
        <InboxFileQueue
          items={items}
          busy={busy}
          onAdd={addFiles}
          onRemove={(id) => { if (!sending.current) setItems((current) => current.filter((item) => item.id !== id)); }}
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" disabled={busy || !owner || !sendable.length} onClick={() => void send()}>{busy ? "Slanje…" : `Pošalji${sendable.length ? ` (${sendable.length})` : ""}`}</Button>
        {!obligation && <Button type="button" variant="outline" disabled={busy || items.length > 0} onClick={nextOwner}>Sljedeći</Button>}
        {items.length > 0 ? <p className="text-sm text-muted-foreground">Prije sljedećeg nositelja pošaljite ili uklonite preostale datoteke.</p> : null}
      </div>
      {outcomes.length > 0 ? <section aria-label="Rezultati slanja" aria-live="polite" className="avm-surface rounded-[var(--radius-card)] border border-border bg-card p-4 md:p-6">
        <h2 className="font-semibold">Rezultati slanja</h2>
        <p className="my-2">Zaprimljeno: {outcomes.filter((item) => item.status === "success" && !item.undone).length} · Duplikati: {outcomes.filter((item) => item.status === "duplicate").length} · Neuspjelo: {outcomes.filter((item) => item.status === "error").length}</p>
        <ul className="flex flex-col gap-2">{outcomes.map((item) => (
          <li key={item.id} className={`flex flex-wrap items-center gap-3 rounded border p-2 break-words ${previewId === item.id ? "border-primary bg-muted" : "border-transparent"}`}>
            {item.preview ? <button type="button" aria-pressed={previewId === item.id} onClick={() => setPreviewId(item.id)} className="min-h-11 min-w-0 flex-1 break-all text-left underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2">
              <strong>{item.name}</strong> · {item.message}
            </button> : <span><strong>{item.name}</strong> · {item.message}</span>}
            {item.previewError && <p className="text-sm" role="status">{item.previewError}</p>}
            {canDelete && item.inboxId && !item.undone ? (
              <Button type="button" variant="outline" disabled={undoing.has(item.id)} onClick={() => setUndoTarget(item)}>
                {undoing.has(item.id) ? "Poništavam…" : "Poništi"}
              </Button>
            ) : null}
          </li>
        ))}</ul>
      </section> : null}
      </div>
      {undoTarget && <UploadUndoDialog name={undoTarget.name} busy={undoing.has(undoTarget.id)} onClose={() => setUndoTarget(null)} onConfirm={(reason) => void undo(undoTarget, reason)} />}
      {!compact && (activePreview?.preview ? <InboxUploadPreview
        name={activePreview.name}
        preview={activePreview.preview}
        canUndo={canDelete && Boolean(activePreview.inboxId) && !activePreview.undone}
        undoing={undoing.has(activePreview.id)}
        onUndo={() => setUndoTarget(activePreview)}
        onClose={() => setPreviewId(null)}
      /> : <aside aria-label="Mjesto za pregled" className="hidden min-h-0 rounded-[var(--radius-card)] border border-border bg-card p-6 lg:block">
        <h2 className="font-semibold">Pregled</h2>
        <p className="mt-3 text-muted-foreground">Pošaljite dokument ili odaberite redak rezultata.</p>
      </aside>)}
      </div>
    </div>
  );
}

function UploadUndoDialog({ name, busy, onClose, onConfirm }: { name: string; busy: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const ref = useRef<HTMLDialogElement>(null); const [reason, setReason] = useState("");
  useEffect(() => { const dialog = ref.current; dialog?.showModal(); return () => dialog?.close(); }, []);
  return <dialog ref={ref} aria-label="Obriši dokument" className="fixed inset-0 m-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-lg space-y-4 overflow-auto rounded-lg border bg-background p-4 text-foreground shadow-xl backdrop:bg-black/40" onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}>
    <h2>Obriši dokument</h2><p>{name}</p><p>Dokument se uklanja iz ladice. Može se vratiti uz filtar Prikaži obrisane.</p>
    <label>Razlog brisanja (neobavezno)<Input maxLength={500} value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)} /></label>
    <Button variant="danger" disabled={busy} onClick={() => onConfirm(reason)}>Potvrdi brisanje</Button><Button autoFocus variant="outline" disabled={busy} onClick={onClose}>Odustani</Button>
  </dialog>;
}
